import React, { useState, useEffect } from 'react';
import { 
    Search, CheckCircle, AlertTriangle, ArrowLeft, Edit, XCircle, ChevronDown, ChevronRight, Users, User, LogIn, Ban, Check, Clock, RefreshCw, Building2, X, Plus 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const HistoriqueReservations = () => {
  const navigate = useNavigate();
  const [listeReservations, setListeReservations] = useState([]);
  const [reglesTarifs, setReglesTarifs] = useState([]);
  const [clientsBD, setClientsBD] = useState([]);
  const [civilitesList, setCivilitesList] = useState([]);
  const [societesList, setSocietesList] = useState([]);
  
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('EN_COURS');
  const [lignesEtendues, setLignesEtendues] = useState({});
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [conflitMessages, setConflitMessages] = useState({});
  
  // 🏢 États pour l'affectation ou la modification de société dans l'onglet Clôturé
  const [showAffecterSocieteId, setShowAffecterSocieteId] = useState(null);
  const [societeSelectionneeForm, setSocieteSelectionneeForm] = useState({});

  const [activeCheckInLigneId, setActiveCheckInLigneId] = useState(null);
  const [checkInForm, setCheckInForm] = useState({
    client_id: '',
    client_nom: '',
    civilite_id: '',
    client_telephone: '',
    contact: '',
    email: '',
    adresse: '',
    nif: '',
    piece_identite_type: 'CNI',
    piece_identite_numero: '',
    nombre_adultes: 1,
    nombre_enfants: 0,
    is_early_checkin: false,
    regle_tarif_id: '',
    early_checkin_montant: 0,
    date_arrivee: '',
    date_depart_prevue: ''
  });
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [activeNoShowLigneId, setActiveNoShowLigneId] = useState(null);
  const [noShowForm, setNoShowForm] = useState({
    type_noshow: 'SANS_PENALITE',
    regle_tarif_id: '',
    montant_penalite: 0
  });
  const [activeMutationLigneId, setActiveMutationLigneId] = useState(null);
  const [chambresDispos, setChambresDispos] = useState([]);
  const [mutationForm, setMutationForm] = useState({
    nouvelle_chambre_id: '',
    date_debut: '',
    date_fin: ''
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const dateDuJour = new Date().toISOString().split('T')[0];

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const toArray = (response, keys = []) => {
    const raw = response?.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') {
      for (const key of keys) {
        if (Array.isArray(raw[key])) return raw[key];
      }
      if (Array.isArray(raw.data)) return raw.data;
    }
    return [];
  };

  const fetchData = async () => {
    try {
      const [resResponse, reglesResponse, dbClients, civilitesRes, societesRes] = await Promise.all([
        API.get('/hebergement/reservations'),
        API.get('/hebergement/regles-tarifs'),
        API.get('/customers').catch(() => ({ data: [] })),
        API.get('/hebergement/civilites').catch(() => ({ data: [] })),
        API.get('/hebergement/societes').catch(() => ({ data: [] }))
      ]);
      setListeReservations(resResponse.data || []);
      setReglesTarifs(reglesResponse.data || []);
      setClientsBD(toArray(dbClients, ['clients', 'customers', 'data']));
      setCivilitesList(toArray(civilitesRes, ['civilites', 'data']));
      setSocietesList(toArray(societesRes, ['societes', 'data']));
    } catch (err) {
      console.error("Erreur chargement données:", err);
      showToast("Erreur lors du chargement des données", "error");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 🏢 Fonction pour rattacher une société sur un séjour clôturé
  const handleRattacherSocieteCloturee = async (e, reservationId) => {
    e.preventDefault();
    const societeId = societeSelectionneeForm[reservationId];
    if (!societeId) {
      showToast("Veuillez sélectionner une société partenaire", "error");
      return;
    }

    try {
      setLoading(true);
      await API.put(`/hebergement/reservations/${reservationId}/societe`, { societe_id: societeId });
      showToast("Société rattachée avec succès !");
      setShowAffecterSocieteId(null);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors du rattachement de la société", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🏢 Fonction pour détacher une société sur un séjour clôturé
  const handleDetacherSocieteCloturee = async (reservationId) => {
    try {
      setLoading(true);
      await API.delete(`/hebergement/reservations/${reservationId}/societe`);
      showToast("Société détachée avec succès !");
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors du détachement de la société", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAnnulerReservation = async (reservationId) => {
    try {
      await API.delete(`/hebergement/reservations/${reservationId}`);
      showToast("Réservation annulée avec succès");
      setConfirmCancelId(null);
      fetchData();
    } catch (err) {
      console.error("Erreur annulation:", err);
      showToast(err.response?.data?.error || "Erreur lors de l'annulation", "error");
      setConfirmCancelId(null);
    }
  };

  const handleNoShowGlobalIndividuelle = async (res) => {
    if (!res.chambres || res.chambres.length === 0) return;
    const premiereChambre = res.chambres[0];
    try {
      setLoading(true);
      await API.put(`/hebergement/reservations/lignes/${premiereChambre.id}/noshow`, {
        type_noshow: 'SANS_PENALITE',
        regle_tarif_id: null,
        montant_penalite: 0,
        reservation_id: res.id,
        chambre_id: premiereChambre.chambre_id,
        client_nom: premiereChambre.client_nom || res.reservant_nom
      });
      showToast("Réservation basculée en No-Show avec succès.");
      fetchData();
    } catch (err) {
      console.error("Erreur No-Show global:", err);
      showToast(err.response?.data?.error || "Erreur lors du passage en No-Show.", "error");
    } finally {
      setLoading(false);
    }
  };

  const reinitialiserInformationsClient = (nom = '') => {
    setCheckInForm(prev => ({
      ...prev,
      client_id: '',
      client_nom: nom,
      civilite_id: '',
      client_telephone: '',
      contact: '',
      email: '',
      adresse: '',
      nif: '',
      piece_identite_type: 'CNI',
      piece_identite_numero: '',
      nombre_adultes: 1,
      nombre_enfants: 0
    }));
  };

  const verifierClientSaisi = () => {
    const nomSaisi = checkInForm.client_nom.trim().toLowerCase();
    if (!nomSaisi) {
      reinitialiserInformationsClient('');
      setShowClientDropdown(false);
      return;
    }

    const clientTrouve = clientsBD.find(c => 
      (c.nom || '').trim().toLowerCase() === nomSaisi
    );

    if (!clientTrouve) {
      reinitialiserInformationsClient(checkInForm.client_nom.trim());
      setShowClientDropdown(false);
      return;
    }

    setCheckInForm(prev => ({
      ...prev,
      client_id: clientTrouve.id || '',
      client_nom: clientTrouve.nom || '',
      civilite_id: clientTrouve.civilite_id || '',
      client_telephone: clientTrouve.telephone || '',
      contact: clientTrouve.contact || '',
      email: clientTrouve.email || '',
      adresse: clientTrouve.adresse || '',
      nif: clientTrouve.nif ?? '',
      piece_identite_type: clientTrouve.piece_identite_type || 'CNI',
      piece_identite_numero: clientTrouve.piece_identite_numero || ''
    }));

    setShowClientDropdown(false);
  };

  const toggleCheckInInline = async (chambreLigne, reservation) => {
    if (activeCheckInLigneId === chambreLigne.id) {
      setActiveCheckInLigneId(null);
    } else {
      setActiveCheckInLigneId(chambreLigne.id);
      setActiveNoShowLigneId(null);
      setActiveMutationLigneId(null);
      
      const nomRecherche = (chambreLigne.client_nom || reservation.reservant_nom || '').trim().toLowerCase();
      const telRecherche = (chambreLigne.client_telephone || reservation.reservant_telephone || '').trim();

      const clientTrouve = clientsBD.find(c => 
        (telRecherche && c.telephone === telRecherche) || 
        (c.nom && c.nom.toLowerCase() === nomRecherche)
      );

      setCheckInForm({
        client_id: clientTrouve ? clientTrouve.id : '',
        client_nom: clientTrouve ? clientTrouve.nom : (chambreLigne.client_nom || reservation.reservant_nom || ''),
        civilite_id: clientTrouve ? (clientTrouve.civilite_id || '') : '',
        client_telephone: clientTrouve ? (clientTrouve.telephone || '') : (chambreLigne.client_telephone || reservation.reservant_telephone || ''),
        contact: clientTrouve?.contact || '',
        email: clientTrouve?.email || '',
        adresse: clientTrouve?.adresse || '',
        nif: clientTrouve?.nif || '',
        piece_identite_type: clientTrouve?.piece_identite_type || 'CNI',
        piece_identite_numero: clientTrouve?.piece_identite_numero || '', 
        nombre_adultes: 1,
        nombre_enfants: 0,
        is_early_checkin: false,
        regle_tarif_id: '',
        early_checkin_montant: 0,
        date_arrivee: dateDuJour,
        date_depart_prevue: chambreLigne.date_check_out ? chambreLigne.date_check_out.split('T')[0] : (reservation.date_depart_prevue ? reservation.date_depart_prevue.split('T')[0] : '')
      });

      try {
        const resCheck = await API.get(`/hebergement/reservations/lignes/${chambreLigne.id}/verifier-conflit`, {
          params: { date_arrivee: dateDuJour }
        });

        if (resCheck.data && resCheck.data.conflit_message) {
          setConflitMessages(prev => ({ ...prev, [chambreLigne.id]: resCheck.data.conflit_message }));
        } else {
          setConflitMessages(prev => ({ ...prev, [chambreLigne.id]: null }));
        }
      } catch (err) {
        console.warn("Vérification de conflit optionnelle non bloquante:", err);
      }
    }
  };

  const clientsFiltres = (Array.isArray(clientsBD) ? clientsBD : []).filter(c => {
    if (!c) return false;
    const rechercheNom = (checkInForm.client_nom || '').toLowerCase();
    return (c.nom && String(c.nom).toLowerCase().includes(rechercheNom)) || (c.telephone && String(c.telephone).includes(checkInForm.client_nom));
  }).slice(0, 10);

  const toggleNoShowInline = (chambreLigne) => {
    if (activeNoShowLigneId === chambreLigne.id) {
      setActiveNoShowLigneId(null);
    } else {
      setActiveNoShowLigneId(chambreLigne.id);
      setActiveCheckInLigneId(null);
      setActiveMutationLigneId(null);
      setNoShowForm({
        type_noshow: 'SANS_PENALITE',
        regle_tarif_id: '',
        montant_penalite: 0
      });
    }
  };

  const handleOuvrirMutation = async (chambreLigne, reservationId) => {
    if (activeMutationLigneId === chambreLigne.id) {
      setActiveMutationLigneId(null);
    } else {
      setActiveMutationLigneId(chambreLigne.id);
      setActiveCheckInLigneId(null);
      setActiveNoShowLigneId(null);
      
      const dateDebutDefaut = dateDuJour;
      const dateFinDefaut = chambreLigne.date_check_out ? chambreLigne.date_check_out.split('T')[0] : '';

      setMutationForm({
        nouvelle_chambre_id: '',
        date_debut: dateDebutDefaut,
        date_fin: dateFinDefaut
      });

      try {
        const response = await API.get('/hebergement/reservations/disponibles', {
          params: {
            arrivee: dateDebutDefaut,
            depart: dateFinDefaut,
            exclude_reservation_id: reservationId
          }
        });
        const dataDispo = response.data.disponibles || response.data || [];
        setChambresDispos(Array.isArray(dataDispo) ? dataDispo : []);
      } catch (err) {
        console.error("Erreur récupération chambres disponibles:", err);
        showToast("Erreur lors de la récupération des chambres disponibles", "error");
      }
    }
  };

  const handleValiderMutation = async (e, ligneId) => {
    e.preventDefault();
    if (!mutationForm.nouvelle_chambre_id) {
      showToast("Veuillez sélectionner une chambre de substitution.", "error");
      return;
    }

    try {
      setLoading(true);
      await API.put(`/hebergement/reservations/lignes/${ligneId}/muter`, mutationForm);
      showToast("Mutation de la chambre effectuée avec succès !");
      setActiveMutationLigneId(null);
      fetchData();
    } catch (err) {
      console.error("Erreur mutation:", err);
      showToast(err.response?.data?.error || "Erreur lors de la mutation de la chambre.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getReglesDisponiblesPourChambre = (chambre) => {
    const reglesFiltrees = reglesTarifs.filter(r => r.categorie_id === chambre.categorie_id);
    if (reglesFiltrees.length > 0) return reglesFiltrees;
    return reglesTarifs;
  };

  const handleRegleChange = (e, tarifChambre) => {
    const regleId = e.target.value;
    const regleTrouvee = reglesTarifs.find(r => r.id === regleId);
    if (!regleTrouvee) {
      setCheckInForm(prev => ({ ...prev, regle_tarif_id: '', early_checkin_montant: 0 }));
      return;
    }

    let montantCalcule = 0;
    const valeurRegle = parseFloat(regleTrouvee.montant) || 0;
    const mode = (regleTrouvee.mode_valeur || 'FIXE').toUpperCase();

    if (mode === 'POURCENTAGE') {
      montantCalcule = (parseFloat(tarifChambre) * valeurRegle) / 100;
    } else {
      montantCalcule = valeurRegle;
    }

    setCheckInForm(prev => ({ ...prev, regle_tarif_id: regleId, early_checkin_montant: montantCalcule }));
  };

  const handleRegleNoShowChange = (e, tarifChambre) => {
    const regleId = e.target.value;
    const regleTrouvee = reglesTarifs.find(r => r.id === regleId);
    if (!regleTrouvee) {
      setNoShowForm(prev => ({ ...prev, regle_tarif_id: '', montant_penalite: 0 }));
      return;
    }

    let montantCalcule = 0;
    const valeurRegle = parseFloat(regleTrouvee.montant) || 0;
    const mode = (regleTrouvee.mode_valeur || 'FIXE').toUpperCase();

    if (mode === 'POURCENTAGE') {
      montantCalcule = (parseFloat(tarifChambre) * valeurRegle) / 100;
    } else {
      montantCalcule = valeurRegle;
    }

    setNoShowForm(prev => ({ ...prev, regle_tarif_id: regleId, montant_penalite: montantCalcule }));
  };

  const handleValiderCheckInLigne = async (e, ligne, reservationId) => {
    e.preventDefault();
    if (!checkInForm.civilite_id) {
      showToast("La civilité est obligatoire.", "error");
      return;
    }

    if (!checkInForm.client_telephone || checkInForm.client_telephone.trim() === '') {
      showToast("Le numéro de téléphone est obligatoire.", "error");
      return;
    }

    if (!checkInForm.piece_identite_numero || checkInForm.piece_identite_numero.trim() === '') {
      showToast("Le numéro de la pièce d'identité est obligatoire pour le check-in.", "error");
      return;
    }

    try {
      setLoading(true);
      await API.post(`/hebergement/reservations/checkin/${ligne.id}`, checkInForm);

      showToast("Check-in validé avec succès !");
      setActiveCheckInLigneId(null);
      fetchData();
    } catch (err) {
      console.error("Erreur check-in ligne:", err);
      const errorMessage = err.response?.data?.error || err.message || "";

      if (err.response?.status === 400 && (errorMessage.includes('déjà réservée') || errorMessage.includes('occupée') || errorMessage.includes('conflit') || errorMessage.includes('BLOQUAGE'))) {
        showToast(errorMessage, "error");

        setConflitMessages(prev => ({ ...prev, [ligne.id]: errorMessage }));
        setActiveMutationLigneId(ligne.id);

        const dateDebutDefaut = checkInForm.date_arrivee || dateDuJour;
        const dateFinDefaut = checkInForm.date_depart_prevue || '';

        setMutationForm({
          nouvelle_chambre_id: '',
          date_debut: dateDebutDefaut,
          date_fin: dateFinDefaut
        });

        try {
          const response = await API.get('/hebergement/reservations/disponibles', {
            params: { arrivee: dateDebutDefaut, depart: dateFinDefaut, exclude_reservation_id: reservationId }
          });
          const dataDispo = response.data.disponibles || response.data || [];
          setChambresDispos(Array.isArray(dataDispo) ? dataDispo : []);
        } catch (dispoErr) {
          console.error("Erreur chargement chambres dispo:", dispoErr);
        }

      } else {
        showToast(errorMessage || "Erreur lors du check-in.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValiderNoShowLigne = async (e, ligne, reservationId) => {
    e.preventDefault();
    try {
      setLoading(true);
      await API.put(`/hebergement/reservations/lignes/${ligne.id}/noshow`, {
        type_noshow: noShowForm.type_noshow,
        regle_tarif_id: noShowForm.type_noshow === 'AVEC_PENALITE' ? noShowForm.regle_tarif_id : null,
        montant_penalite: noShowForm.type_noshow === 'AVEC_PENALITE' ? Number(noShowForm.montant_penalite || 0) : 0,
        reservation_id: reservationId,
        chambre_id: ligne.chambre_id,
        client_nom: ligne.client_nom || 'Client'
      });
      showToast("Traitement No-Show enregistré avec succès.");
      setActiveNoShowLigneId(null);
      fetchData();
    } catch (err) {
      console.error("Erreur No-Show:", err);
      showToast(err.response?.data?.error || "Erreur lors du traitement No-Show.", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleLigneEtendue = (id) => {
    setLignesEtendues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const reservationsFiltrees = (Array.isArray(listeReservations) ? listeReservations : []).filter(res => {
    const matchTexte = 
      (res.reservant_nom && res.reservant_nom.toLowerCase().includes(recherche.toLowerCase())) ||
      (res.client_principal_nom && res.client_principal_nom.toLowerCase().includes(recherche.toLowerCase())) ||
      (res.reservant_telephone && res.reservant_telephone.includes(recherche)) ||
      (res.id && String(res.id).toLowerCase().includes(recherche.toLowerCase()));
    
    const statutRes = res.statut ? res.statut.toUpperCase() : '';
    const typeRes = (res.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndividuelle = typeRes === 'INDIVIDUELLE' || !res.chambres || res.chambres.length <= 1;

    let matchStatut = false;

    if (estIndividuelle) {
      const stLigne = res.chambres && res.chambres.length > 0 ? (res.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = statutRes || stLigne || 'EN_COURS';

      if (filtreStatut === 'EN_COURS') {
        matchStatut = statutEffectif !== 'ANNULEE' && statutEffectif !== 'NO_SHOW' && statutEffectif !== 'CHECK_OUT' && statutEffectif !== 'CLOS' && statutEffectif !== 'CLOTUREE' && statutEffectif !== 'TERMINEE' && statutEffectif !== 'CHECK_IN';
      } else if (filtreStatut === 'CONFIRMEE') {
        matchStatut = statutEffectif === 'CHECK_IN';
      } else if (filtreStatut === 'NO_SHOW') {
        matchStatut = statutEffectif === 'NO_SHOW';
      } else if (filtreStatut === 'CLOTUREE') {
        matchStatut = statutEffectif === 'CHECK_OUT' || statutEffectif === 'CLOS' || statutEffectif === 'CLOTUREE' || statutEffectif === 'TERMINEE';
      } else if (filtreStatut === 'ANNULEE') {
        matchStatut = statutEffectif === 'ANNULEE';
      }
    } else {
      const toutesCheckout = res.chambres.every(ch => ['CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      const toutesNoShow = res.chambres.every(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW');
      const toutesTraittees = res.chambres.every(ch => ['CHECK_IN', 'NO_SHOW', 'ANNULEE', 'CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));

      if (filtreStatut === 'EN_COURS') {
        matchStatut = statutRes !== 'ANNULEE' && statutRes !== 'NO_SHOW' && !toutesCheckout && !toutesNoShow && !toutesTraittees;
      } else if (filtreStatut === 'CONFIRMEE') {
        matchStatut = statutRes !== 'ANNULEE' && !toutesNoShow && toutesTraittees && !toutesCheckout;
      } else if (filtreStatut === 'NO_SHOW') {
        matchStatut = toutesNoShow || statutRes === 'NO_SHOW';
      } else if (filtreStatut === 'CLOTUREE') {
        matchStatut = toutesCheckout || statutRes === 'CLOTUREE' || statutRes === 'TERMINEE';
      } else if (filtreStatut === 'ANNULEE') {
        matchStatut = statutRes === 'ANNULEE';
      }
    }

    return matchTexte && matchStatut;
  });

  const countEnCours = (Array.isArray(listeReservations) ? listeReservations : []).filter(r => {
    const stRes = (r.statut || '').toUpperCase();
    const typeRes = (r.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndiv = typeRes === 'INDIVIDUELLE' || !r.chambres || r.chambres.length <= 1;
    if (estIndiv) {
      const stLigne = r.chambres && r.chambres.length > 0 ? (r.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = stRes || stLigne || 'EN_COURS';
      return statutEffectif !== 'ANNULEE' && statutEffectif !== 'NO_SHOW' && statutEffectif !== 'CHECK_OUT' && statutEffectif !== 'CLOS' && statutEffectif !== 'CLOTUREE' && statutEffectif !== 'TERMINEE' && statutEffectif !== 'CHECK_IN';
    } else {
      const toutesCheckout = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => ['CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      const toutesNoShow = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW');
      const toutesTraittees = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => ['CHECK_IN', 'NO_SHOW', 'ANNULEE', 'CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      return stRes !== 'ANNULEE' && !toutesCheckout && !toutesNoShow && !toutesTraittees;
    }
  }).length;

  const countConfirmee = (Array.isArray(listeReservations) ? listeReservations : []).filter(r => {
    const stRes = (r.statut || '').toUpperCase();
    const typeRes = (r.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndiv = typeRes === 'INDIVIDUELLE' || !r.chambres || r.chambres.length <= 1;
    if (estIndiv) {
      const stLigne = r.chambres && r.chambres.length > 0 ? (r.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = stRes || stLigne || '';
      return statutEffectif === 'CHECK_IN';
    } else {
      const toutesCheckout = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => ['CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      const toutesNoShow = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW');
      const toutesTraittees = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => ['CHECK_IN', 'NO_SHOW', 'ANNULEE', 'CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      return stRes !== 'ANNULEE' && !toutesNoShow && toutesTraittees && !toutesCheckout;
    }
  }).length;

  const countNoShow = (Array.isArray(listeReservations) ? listeReservations : []).filter(r => {
    const stRes = (r.statut || '').toUpperCase();
    const typeRes = (r.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndiv = typeRes === 'INDIVIDUELLE' || !r.chambres || r.chambres.length <= 1;
    if (estIndiv) {
      const stLigne = r.chambres && r.chambres.length > 0 ? (r.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = stRes || stLigne || '';
      return statutEffectif === 'NO_SHOW';
    } else {
      const toutesNoShow = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW');
      return toutesNoShow || stRes === 'NO_SHOW';
    }
  }).length;

  const countCloturee = (Array.isArray(listeReservations) ? listeReservations : []).filter(r => {
    const stRes = (r.statut || '').toUpperCase();
    const typeRes = (r.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndiv = typeRes === 'INDIVIDUELLE' || !r.chambres || r.chambres.length <= 1;
    if (estIndiv) {
      const stLigne = r.chambres && r.chambres.length > 0 ? (r.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = stRes || stLigne || '';
      return statutEffectif === 'CHECK_OUT' || statutEffectif === 'CLOS' || statutEffectif === 'CLOTUREE' || statutEffectif === 'TERMINEE';
    } else {
      const toutesCheckout = r.chambres && r.chambres.length > 0 && r.chambres.every(ch => ['CHECK_OUT', 'CLOS', 'CLOTUREE', 'TERMINEE'].includes((ch.statut || '').toUpperCase()));
      return toutesCheckout || stRes === 'CLOTUREE' || stRes === 'TERMINEE';
    }
  }).length;

  const countAnnulee = (Array.isArray(listeReservations) ? listeReservations : []).filter(r => {
    const stRes = (r.statut || '').toUpperCase();
    const typeRes = (r.type_reservation || 'INDIVIDUELLE').toUpperCase();
    const estIndiv = typeRes === 'INDIVIDUELLE' || !r.chambres || r.chambres.length <= 1;
    if (estIndiv) {
      const stLigne = r.chambres && r.chambres.length > 0 ? (r.chambres[0].statut || '').toUpperCase() : '';
      const statutEffectif = stRes || stLigne || '';
      return statutEffectif === 'ANNULEE';
    } else {
      return stRes === 'ANNULEE';
    }
  }).length;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, height: '100vh', overflowY: 'hidden', background: '#f1f5f9', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#1e293b' }}>
                📋 GESTION DES RÉSERVATIONS & SÉJOURS
              </h1>
              <div style={{ color: '#64748b', fontSize: '12px' }}>
                Suivi, validation des arrivées et gestion des séjours
              </div>
            </div>
          </div>

          <button onClick={() => navigate('/admin/hebergement/nouvelle-reservation')} style={btnPrimaryStyle}>
            + Nouvelle Réservation
          </button>
        </header>

        <div style={{ padding: '20px 30px', width: '100%', maxWidth: '1600px', margin: '0 auto', boxSizing: 'border-box', flex: 1, overflowY: 'auto' }}>
          <div style={{ ...contentCardStyle, padding: '20px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <div style={{ display: 'flex', background: '#e2e8f0', padding: '4px', borderRadius: '8px', gap: '4px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => setFiltreStatut('EN_COURS')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: filtreStatut === 'EN_COURS' ? 'white' : 'transparent', color: filtreStatut === 'EN_COURS' ? '#1e293b' : '#64748b', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}
                >
                  ⏳ En cours / Arrivées ({countEnCours})
                </button>
                <button 
                  onClick={() => setFiltreStatut('CONFIRMEE')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: filtreStatut === 'CONFIRMEE' ? 'white' : 'transparent', color: filtreStatut === 'CONFIRMEE' ? '#1e293b' : '#64748b', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}
                >
                  ✅ En séjour ({countConfirmee})
                </button>
                <button 
                  onClick={() => setFiltreStatut('NO_SHOW')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: filtreStatut === 'NO_SHOW' ? 'white' : 'transparent', color: filtreStatut === 'NO_SHOW' ? '#b45309' : '#64748b', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}
                >
                  ⚠️ No-Show ({countNoShow})
                </button>
                <button 
                  onClick={() => setFiltreStatut('CLOTUREE')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: filtreStatut === 'CLOTUREE' ? 'white' : 'transparent', color: filtreStatut === 'CLOTUREE' ? '#047857' : '#64748b', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}
                >
                  🏁 Séjours Clôturés ({countCloturee})
                </button>
                <button 
                  onClick={() => setFiltreStatut('ANNULEE')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: filtreStatut === 'ANNULEE' ? 'white' : 'transparent', color: filtreStatut === 'ANNULEE' ? '#991b1b' : '#64748b', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}
                >
                  ❌ Annulées ({countAnnulee})
                </button>
              </div>

              <div style={{ position: 'relative', width: '280px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input 
                  type="text"
                  placeholder="Rechercher par nom, téléphone ou ID..."
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  style={{ ...inputStyle, marginTop: 0, paddingLeft: '32px' }}
                />
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px' }}>
                    <th style={{ padding: '10px', width: '40px', textAlign: 'center' }}></th>
                    <th style={{ padding: '10px' }}>ID / Type</th>
                    <th style={{ padding: '10px' }}>Réservant</th>
                    <th style={{ padding: '10px' }}>Chambres & Occupants</th>
                    <th style={{ padding: '10px' }}>Période Globale</th>
                    <th style={{ padding: '10px' }}>Statut Global</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reservationsFiltrees.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Aucune réservation trouvée dans cet onglet.</td>
                    </tr>
                  ) : (
                    reservationsFiltrees.map((res) => {
                      const estAnnulee = res.statut && res.statut.toUpperCase() === 'ANNULEE';
                      const typeRes = (res.type_reservation || 'INDIVIDUELLE').toUpperCase();
                      const estGroupe = typeRes === 'GROUPE' || (res.chambres && res.chambres.length > 1);
                      const estEtendu = Boolean(lignesEtendues[res.id]);
                      const nbChambres = res.chambres ? res.chambres.length : 1;
                      const isConfirmingThis = confirmCancelId === res.id;

                      const dateArriveeRes = res.date_arrivee_prevue ? res.date_arrivee_prevue.split('T')[0] : '';

                      const nbCheckInFait = res.chambres ? res.chambres.filter(ch => (ch.statut || '').toUpperCase() === 'CHECK_IN').length : 0;
                      const nbNoShowFait = res.chambres ? res.chambres.filter(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW').length : 0;
                      const nbChambresTraitees = nbCheckInFait + nbNoShowFait;

                      const estArriveeAujourdhui = !estAnnulee && nbChambresTraitees === 0 && dateArriveeRes === dateDuJour;
                      const estRetardGlobal = !estAnnulee && nbChambresTraitees === 0 && dateArriveeRes && dateArriveeRes < dateDuJour;

                      const nbTraittees = res.chambres ? res.chambres.filter(ch => {
                          const s = (ch.statut || '').toUpperCase();
                          return s === 'CHECK_IN' || s === 'NO_SHOW' || s === 'ANNULEE' || s === 'CHECK_OUT' || s === 'CLOS';
                      }).length : 0;

                      const estComplet = res.chambres && res.chambres.length > 0 && nbTraittees === res.chambres.length;
                      const estNoShowGlobal = (res.chambres && res.chambres.length > 0 && res.chambres.every(ch => (ch.statut || '').toUpperCase() === 'NO_SHOW')) || res.statut === 'NO_SHOW' || (res.chambres?.[0]?.statut || '').toUpperCase() === 'NO_SHOW';

                      const toutesCheckoutOuClos = res.chambres && res.chambres.length > 0 && res.chambres.every(ch => {
                          const st = (ch.statut || '').toUpperCase();
                          return st === 'CHECK_OUT' || st === 'CLOS';
                      });

                      const estClotureeOuTerminee = toutesCheckoutOuClos || res.statut === 'CLOTUREE' || res.statut === 'TERMINEE' || (res.chambres?.[0]?.statut || '').toUpperCase() === 'CHECK_OUT';
                      const estEnSejour = filtreStatut === 'CONFIRMEE' || (nbTraittees > 0 && !toutesCheckoutOuClos && !estAnnulee && !estClotureeOuTerminee) || (res.chambres?.[0]?.statut || '').toUpperCase() === 'CHECK_IN';

                      const autoriserModificationEtAnnulation = filtreStatut === 'EN_COURS' && !estAnnulee && !estClotureeOuTerminee && !estEnSejour && nbChambresTraitees === 0;

                      const societeLiee = societesList.find(s => s.id === res.societe_id);
                      const isAffecterSocieteOpen = showAffecterSocieteId === res.id;

                      return (
                        <React.Fragment key={res.id}>
                          <tr style={{ borderBottom: '1px solid #f1f5f9', background: estAnnulee ? '#fef2f2' : (estRetardGlobal ? '#fffbeb' : (estArriveeAujourdhui ? '#eff6ff' : (estGroupe ? '#faf5ff' : 'transparent'))) }}>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <button 
                                onClick={() => toggleLigneEtendue(res.id)}
                                style={{ background: '#ede9fe', border: 'none', color: '#7c3aed', width: '24px', height: '24px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                {estEtendu ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                            </td>
                            <td style={{ padding: '10px', fontWeight: '800', color: '#2563eb' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                #{res.id}
                                <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: '800', background: estGroupe ? '#f3e8ff' : '#eff6ff', color: estGroupe ? '#7c3aed' : '#1d4ed8' }}>
                                  {estGroupe ? `GROUPE (${nbChambres} ch.)` : 'INDIVIDUELLE'}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontWeight: '700', color: '#1e293b' }}>{res.reservant_nom}</div>
                              <div style={{ fontSize: '10px', color: '#64748b' }}>{res.reservant_telephone || 'N/C'}</div>
                            </td>
                            <td style={{ padding: '10px', fontWeight: '600', color: '#7c3aed' }}>
                              {nbCheckInFait} / {nbChambres} chambre(s) check-in ({nbTraittees} traitées)
                            </td>
                            <td style={{ padding: '10px', color: '#475569' }}>
                              <strong>{res.date_arrivee_prevue ? res.date_arrivee_prevue.split('T')[0] : ''}</strong> au <strong>{res.date_depart_prevue ? res.date_depart_prevue.split('T')[0] : ''}</strong>
                              {estArriveeAujourdhui && (
                                <div style={{ marginTop: '3px' }}>
                                  <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' }}>
                                    🛎️ Arrivée prévue le {dateArriveeRes}
                                  </span>
                                </div>
                              )}
                              {estRetardGlobal && !estGroupe && (
                                <div style={{ marginTop: '3px' }}>
                                  <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' }}>
                                    ⚠️ No-Show (Date dépassée)
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '800', background: estAnnulee ? '#fee2e2' : (estClotureeOuTerminee ? '#dcfce7' : (estNoShowGlobal ? '#fee2e2' : (estComplet ? '#dcfce7' : '#fef3c7'))), color: estAnnulee ? '#991b1b' : (estClotureeOuTerminee ? '#047857' : (estNoShowGlobal ? '#991b1b' : (estComplet ? '#166534' : '#b45309'))) }}>
                                {estAnnulee ? 'ANNULÉE ❌' : (estClotureeOuTerminee ? 'CLÔTURÉ 🏁' : (estNoShowGlobal ? 'NO-SHOW ⚠️' : (estComplet ? 'COMPLET ✅' : 'EN COURS ⏳')))}
                              </span>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                
                                {/* 🏢 BOUTONS SPÉCIFIQUES POUR L'ONGLET SÉJOURS CLÔTURÉS */}
                                {estClotureeOuTerminee && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {societeLiee ? (
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eff6ff', padding: '3px 6px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                                        <Building2 size={11} color="#1d4ed8" />
                                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e40af' }}>{societeLiee.nom || societeLiee.raison_sociale}</span>
                                        <button 
                                          type="button" 
                                          onClick={() => handleDetacherSocieteCloturee(res.id)}
                                          title="Détacher la société"
                                          style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '3px', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: '3px' }}
                                        >
                                          <X size={10} />
                                        </button>
                                      </div>
                                    ) : (
                                      <button 
                                        onClick={() => setShowAffecterSocieteId(isAffecterSocieteOpen ? null : res.id)}
                                        style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                      >
                                        <Building2 size={11} /> Rattacher Société
                                      </button>
                                    )}
                                  </div>
                                )}

                                {autoriserModificationEtAnnulation && !isConfirmingThis && (
                                  <>
                                    {nbChambresTraitees === 0 && (
                                      <>
                                        {estRetardGlobal && !estGroupe ? (
                                          <button 
                                            onClick={() => handleNoShowGlobalIndividuelle(res)}
                                            style={{ background: '#d97706', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}
                                          >
                                            <Ban size={12} /> No-Show
                                          </button>
                                        ) : (
                                          <>
                                            <button onClick={() => navigate(`/admin/hebergement/nouvelle-reservation?edit=${res.id}`)} style={{ background: '#e0e7ff', border: 'none', color: '#3730a3', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              <Edit size={12} /> Modifier
                                            </button>
                                            <button onClick={() => setConfirmCancelId(res.id)} style={{ background: '#fee2e2', border: 'none', color: '#b91c1c', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              <XCircle size={12} /> Annuler
                                            </button>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                                {isConfirmingThis && (
                                  <div style={{ display: 'flex', gap: '5px', background: '#fef2f2', padding: '4px', borderRadius: '6px' }}>
                                    <button onClick={() => handleAnnulerReservation(res.id)} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>Oui</button>
                                    <button onClick={() => setConfirmCancelId(null)} style={{ background: '#e2e8f0', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>Non</button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* 🏢 Formulaire de sélection de la société pour l'onglet Clôturé */}
                          {isAffecterSocieteOpen && (
                            <tr>
                              <td colSpan="7" style={{ background: '#f0fdf4', padding: '12px 20px', borderBottom: '1px dashed #bbf7d0' }}>
                                <form onSubmit={(e) => handleRattacherSocieteCloturee(e, res.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                  <span style={{ fontSize: '11px', fontWeight: '900', color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Building2 size={13} /> Rattacher à la société partenaire :
                                  </span>
                                  <select 
                                    style={{ ...inputStyle, width: '260px', marginTop: 0, padding: '5px' }}
                                    value={societeSelectionneeForm[res.id] || ''}
                                    onChange={(e) => setSocieteSelectionneeForm({ ...societeSelectionneeForm, [res.id]: e.target.value })}
                                    required
                                  >
                                    <option value="">-- Choisir une société --</option>
                                    {societesList.map(soc => (
                                      <option key={soc.id} value={soc.id}>
                                        {soc.nom || soc.raison_sociale} {soc.nif ? `(NIF: ${soc.nif})` : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <button type="submit" style={{ padding: '5px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Valider</button>
                                  <button type="button" onClick={() => setShowAffecterSocieteId(null)} style={{ padding: '5px 8px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                                </form>
                              </td>
                            </tr>
                          )}

                          {estEtendu && (
                            <tr style={{ background: '#fcf8ff', borderBottom: '2px solid #e2e8f0' }}>
                              <td colSpan="7" style={{ padding: '12px 20px 16px 50px' }}>
                                <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e9d5ff', padding: '15px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: '900', color: '#6b21a8', marginBottom: '10px', textTransform: 'uppercase' }}>
                                    🏨 Chambres et Gestion Ligne par Ligne (Réservation #{res.id})
                                  </div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                    <thead>
                                      <tr style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9', textAlign: 'left' }}>
                                        <th style={{ padding: '8px' }}>Chambre N°</th>
                                        <th style={{ padding: '8px' }}>Occupant</th>
                                        <th style={{ padding: '8px' }}>Période</th>
                                        <th style={{ padding: '8px' }}>Tarif</th>
                                        <th style={{ padding: '8px' }}>Statut</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {res.chambres.map((ch, idx) => {
                                        const statutLigne = (ch.statut || '').toUpperCase();
                                        const estCheckInLigne = statutLigne === 'CHECK_IN';
                                        const estNoShow = statutLigne === 'NO_SHOW';
                                        const estAnnuleeLigne = statutLigne === 'ANNULEE';
                                        const estCheckOutLigne = statutLigne === 'CHECK_OUT' || statutLigne === 'CLOS' || statutLigne === 'CLOTUREE' || statutLigne === 'TERMINEE';

                                        const isFormOpen = activeCheckInLigneId === ch.id;
                                        const isNoShowOpen = activeNoShowLigneId === ch.id;
                                        const isMutationOpen = activeMutationLigneId === ch.id;

                                        const dateCheckInLigne = ch.date_check_in ? ch.date_check_in.split('T')[0] : '';

                                        const estArriveeLigneAujourdhui = !estNoShow && !estAnnuleeLigne && !estCheckInLigne && !estCheckOutLigne && dateCheckInLigne === dateDuJour;
                                        const estNoShowAutomatique = !estNoShow && !estAnnuleeLigne && !estCheckInLigne && !estCheckOutLigne && dateCheckInLigne && dateCheckInLigne < dateDuJour;

                                        return (
                                          <React.Fragment key={idx}>
                                            <tr style={{ borderBottom: (isFormOpen || isNoShowOpen || isMutationOpen) ? 'none' : '1px solid #faf5ff' }}>
                                              <td style={{ padding: '8px', fontWeight: '800', color: '#5b21b6' }}>
                                                Chambre {ch.chambre_numero || '-'}
                                              </td>
                                              <td style={{ padding: '8px', fontWeight: '700', color: '#1e293b' }}>
                                                {ch.client_nom || '-'}
                                              </td>
                                              <td style={{ padding: '8px', color: '#475569' }}>
                                                {ch.date_check_in ? ch.date_check_in.split('T')[0] : ''} au {ch.date_check_out ? ch.date_check_out.split('T')[0] : ''}
                                                {estArriveeLigneAujourdhui && (
                                                  <div style={{ marginTop: '2px' }}>
                                                    <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 'bold' }}>
                                                      🛎️ Arrivée prévue le {dateCheckInLigne}
                                                    </span>
                                                  </div>
                                                )}
                                                {estNoShowAutomatique && (
                                                  <div style={{ marginTop: '2px' }}>
                                                    <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 'bold' }}>
                                                      ⚠️ No-Show (Retard d'arrivée)
                                                    </span>
                                                  </div>
                                                )}
                                              </td>
                                              <td style={{ padding: '8px', fontWeight: '700', color: '#059669' }}>
                                                {Number(ch.tarif_nuit || 0).toLocaleString()} F
                                              </td>
                                              <td style={{ padding: '8px' }}>
                                                <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '800', background: estAnnuleeLigne ? '#fee2e2' : (estCheckOutLigne ? '#dcfce7' : (estCheckInLigne ? '#dcfce7' : (estNoShow || estNoShowAutomatique ? '#fee2e2' : '#fef3c7'))), color: estAnnuleeLigne ? '#991b1b' : (estCheckOutLigne ? '#047857' : (estCheckInLigne ? '#166534' : (estNoShow || estNoShowAutomatique ? '#991b1b' : '#b45309'))) }}>
                                                  {estAnnuleeLigne ? 'ANNULÉE ❌' : (estCheckOutLigne ? 'CLÔTURÉ / CHECK-OUT 🏁' : (estCheckInLigne ? 'ARRIVÉ ✅' : (estNoShow || estNoShowAutomatique ? 'NO-SHOW ⚠️' : 'EN ATTENTE ⏳')))}
                                                </span>
                                              </td>
                                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                                                  {!estCheckInLigne && !estNoShow && !estAnnulee && !estAnnuleeLigne && !estCheckOutLigne && (
                                                    <>
                                                      <button 
                                                        onClick={() => toggleCheckInInline(ch, res)}
                                                        style={{ background: isFormOpen ? '#475569' : '#10b981', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}
                                                      >
                                                        <LogIn size={11} /> {isFormOpen ? 'Fermer' : 'Check-in'}
                                                      </button>
                                                      <button 
                                                        onClick={() => toggleNoShowInline(ch)}
                                                        style={{ background: isNoShowOpen ? '#475569' : '#f59e0b', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}
                                                      >
                                                        <Ban size={11} /> {isNoShowOpen ? 'Fermer' : 'No-Show'}
                                                      </button>
                                                    </>
                                                  )}
                                                  {estCheckInLigne && <span style={{ fontSize: '10px', color: '#166534', fontWeight: 'bold' }}>Séjour actif</span>}
                                                  {estCheckOutLigne && <span style={{ fontSize: '10px', color: '#047857', fontWeight: 'bold' }}>Séjour terminé</span>}

                                                  {estNoShow && (
                                                    <span style={{ fontSize: '10px', color: '#991b1b', fontWeight: 'bold' }}>
                                                      {ch.montant_penalite > 0 || ch.type_noshow === 'AVEC_PENALITE' 
                                                        ? `No-Show avec pénalité (${Number(ch.montant_penalite || 0).toLocaleString()} F)` 
                                                        : 'No-Show sans pénalité'}
                                                    </span>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>

                                            {/* FORMULAIRE INTÉGRÉ CHECK-IN */}
                                            {isFormOpen && (
                                              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e9d5ff' }}>
                                                <td colSpan="6" style={{ padding: '15px 20px' }}>
                                                  <form onSubmit={(e) => handleValiderCheckInLigne(e, ch, res.id)} style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #cbd5e1', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>

                                                    {(conflitMessages[ch.id] || ch.conflit_message) && (
                                                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: '6px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '800' }}>
                                                          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                                                          <span>{conflitMessages[ch.id] || ch.conflit_message}</span>
                                                        </div>
                                                        <button 
                                                          type="button" 
                                                          onClick={() => handleOuvrirMutation(ch, res.id)}
                                                          style={{ background: '#dc2626', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                                                        >
                                                          <RefreshCw size={12} /> Muter vers une autre chambre
                                                        </button>
                                                      </div>
                                                    )}

                                                    <div style={{ fontSize: '12px', fontWeight: '900', color: '#1e293b', marginBottom: '12px' }}>
                                                      📝 Validation du Check-in (Arrivée anticipée / Turnover) - Chambre {ch.chambre_numero}
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '150px 2fr 2fr', gap: '10px', marginBottom: '10px' }}>
                                                      <div>
                                                        <label style={labelStyle}>Civilité *</label>
                                                        <select 
                                                          value={checkInForm.civilite_id}
                                                          onChange={(e) => setCheckInForm({...checkInForm, civilite_id: e.target.value})}
                                                          style={inputStyle}
                                                          required
                                                        >
                                                          <option value="">-- Choisir --</option>
                                                          {civilitesList.map((civ) => (
                                                            <option key={civ.id} value={civ.id}>
                                                              {civ.abreviation || civ.libelle}
                                                            </option>
                                                          ))}
                                                        </select>
                                                      </div>
                                                      <div style={{ position: 'relative' }}>
                                                        <label style={labelStyle}>Nom complet de l'occupant *</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.client_nom}
                                                          onChange={(e) => {
                                                            const nouveauNom = e.target.value;
                                                            reinitialiserInformationsClient(nouveauNom);
                                                            setShowClientDropdown(nouveauNom.trim().length > 0);
                                                          }}
                                                          onFocus={() => setShowClientDropdown(checkInForm.client_nom.trim().length > 0)}
                                                          onBlur={() => {
                                                            setTimeout(() => {
                                                              verifierClientSaisi();
                                                            }, 150);
                                                          }}
                                                          style={inputStyle}
                                                          required
                                                        />
                                                        {showClientDropdown && clientsFiltres.length > 0 && (
                                                          <div style={dropdownStyle}>
                                                            {clientsFiltres.map((c, idx) => {
                                                              const civObj = civilitesList.find(civ => civ.id === c.civilite_id);
                                                              const abreviationClient = civObj ? (civObj.abreviation || civObj.libelle) : '';
                                                              return (
                                                                <div 
                                                                  key={c.id || idx} 
                                                                  onClick={() => { 
                                                                    setCheckInForm(prev => ({ 
                                                                      ...prev, 
                                                                      client_id: c.id || '', 
                                                                      client_nom: c.nom || '',
                                                                      civilite_id: c.civilite_id || '',
                                                                      client_telephone: c.telephone || '',
                                                                      contact: c.contact || '',
                                                                      email: c.email || '',
                                                                      adresse: c.adresse || '',
                                                                      nif: c.nif ?? '',
                                                                      piece_identite_type: c.piece_identite_type || 'CNI',
                                                                      piece_identite_numero: c.piece_identite_numero || ''
                                                                    })); 
                                                                    setShowClientDropdown(false); 
                                                                  }} 
                                                                  style={dropdownItemStyle}
                                                                >
                                                                  {abreviationClient && <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '6px' }}>[{abreviationClient}]</span>}
                                                                  <strong>{c.nom ? c.nom.toUpperCase() : ''}</strong> ({c.telephone || 'Sans tél'})
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Téléphone * (Chiffres uniquement)</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.client_telephone}
                                                          onChange={(e) => setCheckInForm({...checkInForm, client_telephone: e.target.value.replace(/\D/g, '')})}
                                                          style={{ ...inputStyle, borderColor: !checkInForm.client_telephone ? '#ef4444' : '#cbd5e1' }}
                                                          placeholder="Ex: 90000000"
                                                          required
                                                        />
                                                      </div>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                                                      <div>
                                                        <label style={labelStyle}>Contact (Secondaire / Autre)</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.contact}
                                                          onChange={(e) => setCheckInForm({...checkInForm, contact: e.target.value})}
                                                          style={inputStyle}
                                                        />
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Email</label>
                                                        <input 
                                                          type="email" 
                                                          value={checkInForm.email}
                                                          onChange={(e) => setCheckInForm({...checkInForm, email: e.target.value})}
                                                          style={inputStyle}
                                                        />
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Adresse</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.adresse}
                                                          onChange={(e) => setCheckInForm({...checkInForm, adresse: e.target.value})}
                                                          style={inputStyle}
                                                        />
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>NIF</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.nif}
                                                          onChange={(e) => setCheckInForm({...checkInForm, nif: e.target.value})}
                                                          style={inputStyle}
                                                        />
                                                      </div>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                                      <div>
                                                        <label style={labelStyle}>Type de Pièce</label>
                                                        <select 
                                                          value={checkInForm.piece_identite_type}
                                                          onChange={(e) => setCheckInForm({...checkInForm, piece_identite_type: e.target.value})}
                                                          style={inputStyle}
                                                        >
                                                          <option value="CNI">CNI</option>
                                                          <option value="PASSEPORT">Passeport</option>
                                                          <option value="PERMIS">Permis</option>
                                                        </select>
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>N° de Pièce d'identité * (Alphanumérique)</label>
                                                        <input 
                                                          type="text" 
                                                          value={checkInForm.piece_identite_numero}
                                                          onChange={(e) => setCheckInForm({...checkInForm, piece_identite_numero: e.target.value})}
                                                          style={{ ...inputStyle, borderColor: !checkInForm.piece_identite_numero ? '#ef4444' : '#cbd5e1' }}
                                                          placeholder="Ex: CNI12345 ou P1234567"
                                                          required
                                                        />
                                                      </div>
                                                    </div>

                                                    {/* SECTION : NOMBRE D'OCCUPANTS (ADULTES & ENFANTS) */}
                                                    <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                                                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#334155', marginBottom: '8px', textTransform: 'uppercase' }}>
                                                        👥 Nombre d'occupants dans la chambre
                                                      </div>
                                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                        <div>
                                                          <label style={labelStyle}>Adultes *</label>
                                                          <input 
                                                            type="number" 
                                                            min="1" 
                                                            value={checkInForm.nombre_adultes}
                                                            onChange={(e) => setCheckInForm({...checkInForm, nombre_adultes: parseInt(e.target.value) || 1})}
                                                            style={inputStyle}
                                                            required 
                                                          />
                                                        </div>
                                                        <div>
                                                          <label style={labelStyle}>Enfants</label>
                                                          <input 
                                                            type="number" 
                                                            min="0" 
                                                            value={checkInForm.nombre_enfants}
                                                            onChange={(e) => setCheckInForm({...checkInForm, nombre_enfants: parseInt(e.target.value) || 0})}
                                                            style={inputStyle}
                                                            required 
                                                          />
                                                        </div>
                                                      </div>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                                      <div>
                                                        <label style={labelStyle}>Date d'arrivée effective (Bloquée à aujourd'hui)</label>
                                                        <input 
                                                          type="date" 
                                                          value={checkInForm.date_arrivee}
                                                          disabled
                                                          style={{ ...inputStyle, background: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' }}
                                                        />
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Date de départ prévue</label>
                                                        <input 
                                                          type="date" 
                                                          value={checkInForm.date_depart_prevue}
                                                          onChange={(e) => setCheckInForm({...checkInForm, date_depart_prevue: e.target.value})}
                                                          style={inputStyle}
                                                          required
                                                        />
                                                      </div>
                                                    </div>

                                                    <div style={{ background: '#fdf4ff', border: '1px solid #f0abfc', padding: '10px 12px', borderRadius: '6px', marginBottom: '15px' }}>
                                                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '11px', color: '#86198f' }}>
                                                        <input 
                                                          type="checkbox" 
                                                          checked={checkInForm.is_early_checkin}
                                                          onChange={(e) => setCheckInForm({...checkInForm, is_early_checkin: e.target.checked})}
                                                          style={{ width: '15px', height: '15px', accentColor: '#a855f7' }}
                                                        />
                                                        <Clock size={13} /> Appliquer un Early Check-in (Arrivée anticipée avec grille tarifaire)
                                                      </label>

                                                      {checkInForm.is_early_checkin && (
                                                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#701a75' }}>
                                                            Sélectionner la règle / grille tarifaire applicable :
                                                          </label>
                                                          <select 
                                                            value={checkInForm.regle_tarif_id}
                                                            onChange={(e) => handleRegleChange(e, ch.tarif_nuit)}
                                                            style={{ ...inputStyle, background: '#fff', borderColor: '#f0abfc' }}
                                                            required={checkInForm.is_early_checkin}
                                                          >
                                                            <option value="">-- Choisir une règle tarifaire --</option>
                                                            {getReglesDisponiblesPourChambre(ch).map(regle => (
                                                              <option key={regle.id} value={regle.id}>
                                                                {regle.libelle} ({regle.montant} {regle.mode_valeur === 'POURCENTAGE' ? '%' : 'F'} - {regle.mode_valeur}) {regle.categorie_nom ? `[Catégorie: ${regle.categorie_nom}]` : '[Général]'}
                                                              </option>
                                                            ))}
                                                          </select>
                                                          {checkInForm.early_checkin_montant > 0 && (
                                                            <div style={{ fontSize: '11px', color: '#047857', fontWeight: 'bold', marginTop: '2px' }}>
                                                              Montant de la majoration appliqué : {Number(checkInForm.early_checkin_montant).toLocaleString()} F CFA
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>

                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                                                      <button 
                                                        type="button" 
                                                        onClick={() => setActiveCheckInLigneId(null)}
                                                        style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }}
                                                      >
                                                        Annuler
                                                      </button>
                                                      <button 
                                                        type="submit" 
                                                        disabled={loading}
                                                        style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '12px', opacity: loading ? 0.7 : 1 }}
                                                      >
                                                        {loading ? 'Validation...' : 'Valider l\'entrée en chambre'}
                                                      </button>
                                                    </div>
                                                  </form>
                                                </td>
                                              </tr>
                                            )}

                                            {/* FORMULAIRE / MODAL DE MUTATION DE CHAMBRE */}
                                            {isMutationOpen && (
                                              <tr style={{ background: '#fffbeb', borderBottom: '2px solid #fcd34d' }}>
                                                <td colSpan="6" style={{ padding: '15px 20px' }}>
                                                 <form onSubmit={(e) => handleValiderMutation(e, ch.id)} style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <div style={{ fontSize: '12px', fontWeight: '950', color: '#92400e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                      <RefreshCw size={14} /> Muter / Changer la chambre {ch.chambre_numero} (Le client est arrivé en avance, la chambre est occupée ou réservée plus tôt)
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                                      <div>
                                                        <label style={labelStyle}>Chambre de substitution disponible *</label>
                                                        <select 
                                                          value={mutationForm.nouvelle_chambre_id}
                                                          onChange={(e) => setMutationForm({...mutationForm, nouvelle_chambre_id: e.target.value})}
                                                          style={{ ...inputStyle, borderColor: '#f59e0b', background: '#fffdf5' }}
                                                          size={chambresDispos.length >= 10 ? 10 : undefined}
                                                          required
                                                        >
                                                          <option value="">-- Sélectionner une chambre libre --</option>
                                                          {chambresDispos.map(c => {
                                                            const prixChambre = c.tarif_nuit !== undefined && c.tarif_nuit !== null ? c.tarif_nuit : (c.tarif_base || c.tarif || 0);
                                                            return (
                                                              <option key={c.id} value={c.id}>
                                                                Chambre {c.numero || c.chambre_numero} ({c.categorie_nom || 'Standard'}) - {Number(prixChambre).toLocaleString()} F
                                                              </option>
                                                            );
                                                          })}
                                                        </select>
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Du (Début de mutation)</label>
                                                        <input 
                                                          type="date" 
                                                          value={mutationForm.date_debut}
                                                          onChange={(e) => setMutationForm({...mutationForm, date_debut: e.target.value})}
                                                          style={inputStyle}
                                                          required
                                                        />
                                                      </div>
                                                      <div>
                                                        <label style={labelStyle}>Au (Fin de mutation / provisoire)</label>
                                                        <input 
                                                          type="date" 
                                                          value={mutationForm.date_fin}
                                                          onChange={(e) => setMutationForm({...mutationForm, date_fin: e.target.value})}
                                                          style={inputStyle}
                                                          required
                                                        />
                                                      </div>
                                                    </div>

                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                      <button 
                                                        type="button" 
                                                        onClick={() => setActiveMutationLigneId(null)}
                                                        style={{ background: '#e2e8f0', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '11px' }}
                                                      >
                                                        Annuler
                                                      </button>
                                                      <button 
                                                        type="submit" 
                                                        disabled={loading}
                                                        style={{ background: '#d97706', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}
                                                      >
                                                        {loading ? 'Mutation...' : 'Confirmer la mutation de chambre'}
                                                      </button>
                                                    </div>
                                                 </form>
                                                </td>
                                              </tr>
                                            )}

                                            {/* FORMULAIRE INTÉGRÉ NO-SHOW */}
                                            {isNoShowOpen && (
                                              <tr style={{ background: '#fffbeb', borderBottom: '2px solid #fde68a' }}>
                                                <td colSpan="6" style={{ padding: '15px 20px' }}>
                                                  <form onSubmit={(e) => handleValiderNoShowLigne(e, ch, res.id)} style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <div style={{ fontSize: '12px', fontWeight: '900', color: '#92400e', marginBottom: '12px' }}>
                                                      ⚠️ Traitement No-Show - Chambre {ch.chambre_numero} ({ch.client_nom || res.reservant_nom})
                                                    </div>

                                                    <div style={{ marginBottom: '12px' }}>
                                                      <label style={labelStyle}>Sélectionner le mode de No-Show :</label>
                                                      <select 
                                                        value={noShowForm.type_noshow}
                                                        onChange={(e) => setNoShowForm({...noShowForm, type_noshow: e.target.value})}
                                                        style={inputStyle}
                                                      >
                                                        <option value="SANS_PENALITE">No-Show Sans Pénalité (Annulation simple de la chambre)</option>
                                                        <option value="AVEC_PENALITE">No-Show Avec Pénalité (Appliquer une grille tarifaire sur la facture)</option>
                                                      </select>
                                                    </div>

                                                    {noShowForm.type_noshow === 'AVEC_PENALITE' && (
                                                      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', padding: '10px 12px', borderRadius: '6px', marginBottom: '15px' }}>
                                                        <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#c2410c' }}>
                                                          Sélectionner la règle / grille tarifaire de pénalité :
                                                        </label>
                                                        <select 
                                                          value={noShowForm.regle_tarif_id}
                                                          onChange={(e) => handleRegleNoShowChange(e, ch.tarif_nuit)}
                                                          style={{ ...inputStyle, background: '#fff', borderColor: '#fdba74' }}
                                                          required={noShowForm.type_noshow === 'AVEC_PENALITE'}
                                                        >
                                                          <option value="">-- Choisir une grille tarifaire --</option>
                                                          {getReglesDisponiblesPourChambre(ch).map(regle => (
                                                            <option key={regle.id} value={regle.id}>
                                                              {regle.libelle} ({regle.montant} {regle.mode_valeur === 'POURCENTAGE' ? '%' : 'F'} - {regle.mode_valeur})
                                                            </option>
                                                          ))}
                                                        </select>
                                                        {noShowForm.montant_penalite > 0 && (
                                                          <div style={{ fontSize: '11px', color: '#c2410c', fontWeight: 'bold', marginTop: '4px' }}>
                                                            Montant de la pénalité à facturer : {Number(noShowForm.montant_penalite).toLocaleString()} F CFA
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}

                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                      <button 
                                                        type="button" 
                                                        onClick={() => setActiveNoShowLigneId(null)}
                                                        style={{ background: '#e2e8f0', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '11px' }}
                                                      >
                                                        Annuler
                                                      </button>
                                                      <button 
                                                        type="submit" 
                                                        disabled={loading}
                                                        style={{ background: '#d97706', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}
                                                      >
                                                        {loading ? 'Traitement...' : 'Confirmer le No-Show'}
                                                      </button>
                                                    </div>
                                                  </form>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>

        </div>

      </main>

      <style>{`@keyframes slideInToast { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const contentCardStyle = { background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px -1px rgba(0,0,0,0.03)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '800', fontSize: '12px', boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)' };
const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px', marginTop: '3px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#475569' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '150px', overflowY: 'auto' };
const dropdownItemStyle = { padding: '8px 12px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' };
const toastContainerStyle = { position: 'fixed', top: '20px', right: '20px', zIndex: 9999, color: 'white', padding: '10px 20px', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default HistoriqueReservations;